<template>
  <Modal :activated="activated" modal-style="height: 100%" @close="emit('close')">
    <template #header>
      <h3>Synkronisera</h3>
    </template>

    <ToggleComponent v-model="currentFramework" :options="frameworkOptions" tabs class="mb-3">
      <template #header="{ option }">
        <img v-if="option.value === 'wc'" src="/icons/woocommerce-icon.png" class="framework-icon" :class="{ disabled: currentFramework !== option.value }" />
        <img
          v-else-if="option.value === 'shopify'"
          src="/icons/shopify-icon.png"
          class="framework-icon"
          :class="{ disabled: currentFramework !== option.value }"
        />

        {{ option.displayValue }}

        <i
          class="fa fa-refresh ml-1"
          :class="[
            store.settings?.sync[option.value as ECommerceFramework]?.globalAutoSync ? 'auto-sync' : 'icon-disabled',
            {
              disabled: currentFramework !== option.value,
            },
          ]"
          :title="`Global synkroniseringsautomatik ${option.displayValue}: ${store.settings?.sync[option.value as ECommerceFramework]?.globalAutoSync ? 'PÅ' : 'AV'}`"
        />
      </template>
    </ToggleComponent>

    <AccordionComponent title="Visa hjälp" start-collapsed>
      <p>
        För att en artikel ska kunna synkroniseras till en marknadssajt måste vissa krav uppfyllas. En del av dessa krav kan kringgås via inställningar
        (symboliseras av
        <i class="fa fa-mail-reply text-danger ml-1" />
        ). Avpubliceringar och borttagningar lyder inte dessa regler. Finns de på sajten/marknaden oavsett anledning avpubliceras de eller tas de bort.
      </p>

      <div class="mb-3">
        <div>
          <small class="text-info">Global filtrering</small>
        </div>
        För att synkroniseras måste artikeln...
        <ul>
          <li>
            ha <strong> {{ translate('availability status', 'attributes', 'lowercase') }} </strong>
            <i> "{{ translate('sellable', 'terms', 'lowercase') }}" </i> eller
            <i> "{{ translate('to be discontinued', 'terms', 'lowercase') }}" </i>
            <i class="ml-1 fa fa-mail-reply text-danger" />
          </li>
          <li>
            får inte ha en <strong>kollektion</strong> märkt med <i>"exkluderas vid synk"</i> (kan justeras i taxonomiverktyget)
            <i class="ml-1 fa fa-mail-reply text-danger" />
          </li>
        </ul>
      </div>

      <div class="mb-3">
        <div>
          <small class="text-info">Marknadsspecifik filtrering</small>
        </div>
        Var marknad har en förutbestämd kultur (språk och land). En marknadssajt kräver att artikeln har...
        <ul>
          <li>
            <strong>{{ translate('name', 'attributes', 'lowercase') }}</strong> på huvudspråket eller på marknadens språk
          </li>
          <li>
            B2C-marknader: pris för marknadens land. För STM används endast
            <strong>{{ translate('STM Price', 'attributes') }}</strong> och för övriga sajter används
            <strong> {{ translate('recommended price incl VAT', 'attributes', 'lowercase') }}</strong
            >. Shopify använder även <strong> {{ translate('shopify sale price', 'attributes', 'lowercase') }}</strong> om ett sådant finns. B2B-marknader
            använder inte priser från PIM
          </li>
          <li>
            <strong>{{ translate('available in countries', 'attributes', 'lowercase') }}</strong> inkluderar marknadens land
            <i class="ml-1 fa fa-mail-reply text-danger" />
          </li>
          <li>
            B2C-marknader: <strong>{{ translate('sellable web from', 'attributes') }}</strong> måste ha ett datum som har inträffat
            <i class="ml-1 fa fa-mail-reply text-danger" />
          </li>
        </ul>
      </div>

      <p>
        Artiklar som har synkroniserats till en sajt uppdateras automatiskt på den sajten när de redigerats i PIM om autosynken för sajten är på (symboliseras
        av <i class="fa fa-refresh auto-sync" /> resp. <i class="fa fa-refresh icon-disabled" />). Artiklar som inte redan existerar på sajten kommer däremot
        inte automatiskt skapas vid en autosynk. Autosynken kringgår alltid alla regler som kan kringgås för att hålla artikeln uppdaterad oavsett hur den
        hamnade där.
        <br />
        <small class="text-warning font-weight-bold">Observera att ändringar i översättningar eller taxonomiska objekt inte triggar en auto-synk</small>
      </p>

      <p>
        I Shopify finns det till skillnad från WooCommerce bara en version av varje artikel per huvudsajt, med marknadsspecifika priser och översättningar.
        Ändringar på artikelnivå påverkar samtliga marknader. För att undvika att skapa upp produkter av misstag, kommer endast artiklar som är giltiga för
        minst en av dina valda marknader skapas upp (förutsatt att en marknadsåtgärd är vald).
      </p>
    </AccordionComponent>

    <form @submit.prevent>
      <fieldset :disabled="actionPending">
        <!-------------->
        <!-- Settings -->
        <!-------------->
        <section class="settings">
          <div class="row">
            <div class="col">
              <fieldset :disabled="currentSettings.marketSyncAction === 'unpublish'">
                <h5>Åtgärd (artikel)</h5>
                <label
                  v-for="option of productSyncOptions"
                  :key="option.value"
                  class="d-block position-relative"
                  :class="[
                    {
                      selected: productSyncAction === option.value,
                    },
                    productSyncAction,
                  ]"
                >
                  <input v-model="productSyncAction" type="radio" :value="option.value" />
                  {{ option.displayValue }}
                  <TooltipComponent v-if="option.info">
                    <template #trigger>
                      <i
                        class="fa fa-info-circle"
                        :class="{
                          disabled: productSyncAction !== option.value,
                        }"
                      />
                    </template>
                    {{ option.info }}
                  </TooltipComponent>
                </label>
              </fieldset>
            </div>

            <div class="col">
              <fieldset :disabled="currentSettings.marketSyncAction === 'unpublish' || productSyncAction === 'delete'">
                <h5>Artikelstatus</h5>

                <label v-for="option of productStatusOptions" :key="option.value" class="d-block">
                  <input v-model="currentSettings.productStatus" type="radio" :value="option.value" />
                  {{ option.displayValue }}
                  <TooltipComponent v-if="option.info">
                    <template #trigger>
                      <i
                        class="fa fa-info-circle"
                        :class="{
                          disabled: currentSettings.productStatus !== option.value,
                        }"
                      />
                    </template>
                    {{ option.info }}
                  </TooltipComponent>
                </label>
              </fieldset>
            </div>

            <div class="col">
              <fieldset v-if="currentFramework === 'wc'" :disabled="productSyncAction === 'delete'">
                <h5>Lagerstatus</h5>
                <label v-for="option of stockOptions" :key="option.value" class="d-block">
                  <input v-model="wcStockStatus" type="radio" :value="option.value" />
                  {{ option.displayValue }}
                  <TooltipComponent v-if="option.info">
                    <template #trigger>
                      <i
                        class="fa fa-info-circle"
                        :class="{
                          disabled: wcStockStatus !== option.value,
                        }"
                      />
                    </template>
                    <div style="max-width: 320px">
                      {{ option.info }}
                    </div>
                  </TooltipComponent>
                </label>
              </fieldset>
            </div>
          </div>
        </section>

        <div v-if="firebaseProject !== 'production'" class="text-white bg-danger p-2 mb-3">Du befinner dig på {{ firebaseProject }}!</div>

        <fieldset v-if="currentFramework === 'shopify'" :disabled="productSyncAction === 'delete'">
          <section class="settings">
            <div class="row">
              <div class="col">
                <h5>Marknadsåtgärd</h5>

                <label
                  v-for="option of marketSyncOptions"
                  :key="option.value"
                  class="d-block"
                  :class="[
                    {
                      selected: currentSettings.marketSyncAction === option.value,
                    },
                    currentSettings.marketSyncAction,
                  ]"
                >
                  <input v-model="currentSettings.marketSyncAction" type="radio" :value="option.value" />
                  {{ option.displayValue }}

                  <TooltipComponent v-if="option.info">
                    <template #trigger>
                      <i
                        class="fa fa-info-circle"
                        :class="{
                          disabled: currentSettings.marketSyncAction !== option.value,
                        }"
                      />
                    </template>
                    {{ option.info }}
                  </TooltipComponent>
                </label>
              </div>
            </div>
          </section>
        </fieldset>

        <!-- <section class="settings"> -->
        <fieldset :disabled="shopifyMarketSyncAction === 'unpublish' || productSyncAction === 'delete'">
          <AccordionComponent start-collapsed>
            <template #header>
              <div class="d-flex align-items-center">
                <h5 class="mb-0">Ignorera regler</h5>
                <small class="text-danger ml-2"> {{ ignoredRulesArray.map((rule) => translate(rule, 'attributes')).join(', ') }} </small>
              </div>
            </template>
            <small class="d-block pb-2" style="margin-top: -1em"> Du kan välja att kringgå vissa regler vid behov (använd sparsamt)</small>
            <label v-for="ignorableRule of ignorableRules" :key="ignorableRule" class="d-block">
              <input
                v-model="ignoredRules[ignorableRule]"
                type="checkbox"
                :disabled="ignorableRule === 'available in countries' && !currentSettings.marketSyncAction && currentFramework === 'shopify'"
              />
              {{ translate(ignorableRule, 'attributes') }}
            </label>
          </AccordionComponent>
        </fieldset>

        <!-------------------->
        <!-- Select markets -->
        <!-------------------->

        <section>
          <LoaderComponent v-if="loading" />
          <template v-else>
            <h5>Välj marknadssajter</h5>

            <div class="columns">
              <div v-for="site of sitesMatchingCurrentFramework" :key="site.id" class="main-site">
                <div
                  class="main-site-label user-select-none d-inline-block"
                  style="cursor: default"
                  @click="affectsEntireSite ? toggleSite(site.id) : toggleAllMarkets(site.id)"
                >
                  <input
                    v-if="affectsEntireSite"
                    type="checkbox"
                    :checked="!!currentSettings.selectedMarketHandlesBySiteId.get(site.id)"
                    class="pointer-events-none"
                  />
                  {{ site.id }}

                  <a
                    v-if="site.id === 'Sello WC' && selloOverwriteWarning"
                    class="warning ml-1"
                    :href="`/products?_q=${selloOverwriteWarning.skus.join(',')}`"
                    title="Visa artiklar i extern flik"
                  >
                    <i class="fa fa-info-circle mr-1" />{{ selloOverwriteWarning.msg }}
                  </a>
                </div>
                <small v-if="site.id === 'Sello WC'" class="d-block">
                  Sello WC består egentligen av en marknadssajt. Om en artikel med samma artikelnummer synkroniseras till flera kulturer kommer artikelns
                  kulturella data skrivas över
                </small>

                <div
                  v-for="(market, j) of site.markets"
                  :key="j"
                  class="pl-3 mb-1 site"
                  :class="{
                    disabled: !currentSettings.selectedMarketHandlesBySiteId.get(site.id)?.has(market.handle) || affectsEntireSite,
                  }"
                >
                  <label class="mb-0">
                    <input
                      v-if="!affectsEntireSite"
                      type="checkbox"
                      :checked="!!currentSettings.selectedMarketHandlesBySiteId.get(site.id)?.has(market.handle)"
                      :disabled="affectsEntireSite"
                      @input="toggleMarket(site.id, market)"
                    />
                    {{ market.name }}
                    <i
                      class="fa fa-refresh mx-2"
                      :class="[market.autoSync ? (store.settings?.sync[currentFramework]?.globalAutoSync ? 'auto-sync' : 'disabled') : 'icon-disabled']"
                      :title="`Synkroniseringsautomatik ${market.autoSync && store.settings?.sync[currentFramework]?.globalAutoSync ? 'PÅ' : 'AV'} `"
                    />
                    <img :src="getFlag(market.country)" class="small-flag" />
                  </label>
                  <i v-if="refreshing" class="spinner spinner-border spinner-border-sm refresh-spinner ml-2" />

                  <template v-else>
                    <span class="preview position-relative">
                      <a
                        class="ml-3 position-relative"
                        :target="`included-${market.handle}`"
                        :href="
                          preview?.[site.id]?.[market.handle]?.included.length
                            ? `/products?_field_sku=${preview?.[site.id]?.[market.handle]?.included.join(',')}`
                            : undefined
                        "
                      >
                        <TooltipComponent>
                          <template #trigger>
                            <div class="sync font-weight-bold">
                              <i class="fa" :class="productSyncAction === 'delete' ? 'fa-trash' : 'fa-upload'" />
                              <template v-if="productSyncAction !== 'create' && preview?.[site.id]?.[market.handle]?.included.length"> 0 - </template>
                              {{ preview?.[site.id]?.[market.handle]?.included.length }}
                            </div>
                          </template>

                          <template v-if="productSyncAction !== 'create'">
                            Det är okänt hur många artiklar i urvalet som finns på
                            {{ market.name }}.
                            <br />
                          </template>

                          <template v-if="preview?.[site.id]?.[market.handle]?.included.length">
                            <template v-if="productSyncAction !== 'create'"> Upp till </template>

                            {{ preview?.[site.id]?.[market.handle]?.included.length }}
                            artiklar i urvalet kan synkroniseras med
                            {{ market.name }}
                            <div v-if="preview?.[site.id]?.[market.handle]?.included.length" class="mt-2">
                              <a
                                :href="`/products?_field_sku=${preview?.[site.id]?.[market.handle]?.included.join(',')}`"
                                :target="`${site.id}-${market.handle}-included`"
                              >
                                Visa artiklar i extern flik
                                <i class="fa fa-external-link" />
                              </a>
                            </div>
                          </template>
                          <template v-else> Inga artiklar i urvalet kan synkroniseras till {{ market.name }} </template>
                        </TooltipComponent>
                      </a>

                      <a
                        :target="`excluded-${market.handle}`"
                        class="ml-3 position-relative"
                        :href="
                          preview?.[site.id]?.[market.handle]?.excludedCount
                            ? `/products?_field_sku=${Object.values(preview?.[site.id]?.[market.handle]?.excluded || {})
                                .flat()
                                .join(',')}`
                            : undefined
                        "
                      >
                        <TooltipComponent>
                          <template #trigger>
                            <div class="skip font-weight-bold">
                              <i class="fa fa-times" />
                              {{ preview?.[site.id]?.[market.handle]?.excludedCount }}
                            </div>
                          </template>

                          <div v-if="preview?.[site.id]?.[market.handle]?.excluded.status.length">
                            {{ preview?.[site.id]?.[market.handle]?.excluded.status.length }}
                            artiklar i urvalet har ogilitig tillgänglighetsstatus <br />
                          </div>

                          <div v-if="preview?.[site.id]?.[market.handle]?.excluded.marketRequirements.length">
                            {{ preview?.[site.id]?.[market.handle]?.excluded.marketRequirements.length }}
                            artiklar i urvalet uppfyller inte marknadssajtens krav för
                            {{ market.name }}
                            ({{
                              getLocaleString({
                                language: market.language,
                                country: market.country,
                              })
                            }})
                          </div>
                          <div v-if="preview?.[site.id]?.[market.handle]?.excluded.excludedObjects.length">
                            {{ preview?.[site.id]?.[market.handle]?.excluded.excludedObjects.length }}
                            artiklar i urvalet har referens till ett objekt märkt som "exkludera från ecommerce-synk"
                          </div>

                          <div v-if="preview?.[site.id]?.[market.handle]?.excluded.invalidProductType.length">
                            {{ preview?.[site.id]?.[market.handle]?.excluded.invalidProductType.length }}
                            artiklar i urvalet har ogiltig produkttyp
                          </div>

                          <div v-if="preview?.[site.id]?.[market.handle]?.excludedCount" class="mt-2">
                            <a
                              :href="`/products?_field_sku=${getUniqueSkus(preview?.[site.id]?.[market.handle]?.excluded).join(',')}`"
                              :target="`${site.id}-${market.handle}-included`"
                            >
                              Visa artiklar i extern flik
                              <i class="fa fa-external-link" />
                            </a>
                          </div>
                          <template v-else> Inga artiklar i urvalet exkluderas till {{ market.name }} </template>
                        </TooltipComponent>
                      </a>
                    </span>
                  </template>
                </div>
              </div>
            </div>
          </template>
        </section>
      </fieldset>
    </form>

    <template #footer>
      <!------------------------->
      <!-- Progress and report -->
      <!------------------------->

      <!-- Main progress -->
      <AccordionComponent
        v-if="mainProgress && !confirmSyncStart"
        :style="{
          margin: '-1em -1em 0.5em',
          overflow: 'auto',
          maxHeight: 'calc(100% - 1.85em)',
        }"
        class="minimalistic"
        discreet
        flip-arrows
      >
        <template #title>
          Synkrapport
          <template v-if="mainProgress.endDate">
            ({{ translate(mainProgress.currentAction, 'application') }})
            {{ new Date(mainProgress.endDate).toLocaleString() }}
          </template>
        </template>

        <div style="margin-top: -1em" />
        <div
          v-if="!childProgressIsActive"
          class="font-weight-bold"
          :class="{
            'text-danger': mainProgress.successful === false,
          }"
        >
          <small>{{ mainProgress.currentAction }}</small>
        </div>

        <small class="position-relative">
          <TooltipComponent v-if="mainProgress.data?.skipped?.length" direction="up">
            <template #trigger>
              <span class="text-warning font-weight-bold"> Skippade {{ uniqueSkippedEntries.length }} artiklar för samtliga sajter </span>
            </template>
            <div class="popup" style="bottom: 1.5em">
              <div class="scroll-box minimalistic">
                <div v-for="(skipped, i) of uniqueSkippedEntries" :key="i">
                  {{ skipped.sku }}
                  {{ skipped.reason }}
                </div>
              </div>
              <a :href="`/products?_field_sku=${uniqueSkippedEntries.map((e) => e.sku)}?.join(',')}`" target="main-skipped" class="pt-1 d-inline-block">
                Visa artiklar i extern flik
                <i class="fa fa-external-link" />
              </a>
            </div>
          </TooltipComponent>
        </small>

        <div class="mb-3" />

        <!-- Individual Sites Progress -->
        <template v-for="(progress, i) of progressBars" :key="i">
          <SyncProgressComponent v-if="progress.id" :progress="progress" :title="progress.id" class="mb-3">
            <SyncProgressComponent v-for="(child, j) of progress.children" :key="j" class="ml-3" :progress="child">
              <template #before>
                <img class="small-flag mr-1" :src="getFlag(child.id as Country)" :title="child.id?.toUpperCase()" />
              </template>
            </SyncProgressComponent>
          </SyncProgressComponent>
        </template>
      </AccordionComponent>

      <!-- Confirm buttons -->
      <template v-if="confirmSyncStart">
        Vill du synkronisera ({{ stagedAction }}) urvalet med {{ selectedStoreNames }}?
        <div class="mt-2 d-flex gap-2">
          <button class="btn btn-primary" @click="startSync()">Ja</button>
          <button class="btn btn-warning" @click="confirmSyncStart = false">Nej</button>
        </div>
      </template>

      <ActionPendingButton
        v-else
        type="button"
        :pending="actionPending"
        :disabled="!canStartSync"
        :variant="
          currentSettings.marketSyncAction === 'unpublish'
            ? 'warning'
            : productSyncAction === 'create'
              ? 'success'
              : productSyncAction === 'delete'
                ? 'danger'
                : 'primary'
        "
        @click="confirmSyncStart = true"
      >
        Synkronisera ({{ stagedAction }})
        {{ synching.total || total }}
        artiklar
      </ActionPendingButton>
      <template v-if="mainProgress && !confirmSyncStart">
        <button v-if="!mainProgress.finished" class="btn btn-danger ml-2" @click="abort()">Avbryt</button>
        <a v-if="actionPending" class="ml-2" :href="synching.link" target="sync">
          Visa artiklar
          <i class="fa fa-external-link" />
        </a>
      </template>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import AccordionComponent from '@/components/AccordionComponent.vue'
import ActionPendingButton from '@/components/ActionPendingButton.vue'
import Modal from '@/components/ModalComponent.vue'
import LoaderComponent from '@/components/simple/LoaderComponent.vue'
import TooltipComponent from '@/components/simple/TooltipComponent.vue'
import SyncProgressComponent from '@/components/sync/SyncProgressComponent.vue'
import ToggleComponent from '@/components/ToggleComponent.vue'
import { useTools } from '@/composables/useTools'
import { useTranslation } from '@/composables/useTranslation'
import { firebaseProject } from '@/firebase'
import { StringArrayObject } from '@/models/GenericInterfaces'
import { BasicOption, Country } from '@/models/Global'
import Message from '@/models/Message'
import {
  ECommerceFramework,
  IgnorableRule,
  ignorableRules,
  Market,
  MarketSyncAction,
  ProductSyncAction,
  SelloSiteId,
  ShopifyProductStatus,
  Site,
  SyncPreview,
  WCProductStatus,
  WCStockStatus,
} from '@/models/Sync'
import SyncService from '@/services/SyncService'
import { useDefaultStore } from '@/store'
import { Debouncer } from '@/utils/Debouncer'
import { filterUniqueEntries, getLocaleString } from '@/utils/tools'
import { computed, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

// -----------
// -- Setup --
// -----------
const props = defineProps<{
  activated?: boolean
  total: number
}>()

const emit = defineEmits(['close'])

const store = useDefaultStore()
const { translate } = useTranslation()
const { getFlag } = useTools()
const route = useRoute()

const frameworkOptions: BasicOption<ECommerceFramework>[] = [
  { displayValue: 'WooCommerce', value: 'wc' },
  { displayValue: 'Shopify', value: 'shopify' },
] as const

const marketSyncOptions: Array<BasicOption<MarketSyncAction | undefined> & { info?: string }> = [
  { displayValue: 'Publicera', value: 'publish' },
  { displayValue: 'Avpublicera', value: 'unpublish' },
  {
    displayValue: 'Ingen',
    value: undefined,
    info: 'Publicerar inte produkten till någon marknad. Uppdaterar artikeldata på vald(a) huvudsajt(er)',
  },
]

const productSyncOptions: Array<BasicOption<ProductSyncAction> & { info?: string }> = [
  { displayValue: 'Endast uppdatera', value: 'update', info: 'Uppdaterar existerande artiklar, men skapar inga nya' },
  { displayValue: 'Skapa / Uppdatera', value: 'create' },
  { displayValue: 'Ta bort', value: 'delete' },
]

const productStatusOptions = computed(
  (): Array<BasicOption<WCProductStatus | ShopifyProductStatus | undefined> & { info?: string }> =>
    currentFramework.value === 'wc'
      ? [
          {
            displayValue: 'Ospecificerat',
            value: undefined,
            info: 'Vid skapande av nya artiklar kommer status sättas till "publish". I annat fall ändras inte status från artiklarnas nuvarande värde',
          },
          { displayValue: 'Publish', value: 'publish' },
          { displayValue: 'Draft', value: 'draft' },
          { displayValue: 'Pending', value: 'pending' },
          { displayValue: 'Private', value: 'private' },
        ]
      : [
          {
            displayValue: 'Ospecificerat',
            value: undefined,
            info: 'Vid skapande av nya artiklar kommer status sättas till "ACTIVE". I annat fall ändras inte status från artiklarnas nuvarande värde',
          },
          { displayValue: 'ACTIVE', value: 'ACTIVE' },
          { displayValue: 'DRAFT', value: 'DRAFT' },
          { displayValue: 'ARCHIVED', value: 'ARCHIVED' },
        ],
)

const stockOptions: Array<BasicOption<WCStockStatus | undefined> & { info?: string }> = [
  {
    displayValue: 'Ospecificerat',
    value: undefined,
    info: 'Vid skapande av nya artiklar kommer lagerstatus sättas till "i lager". I annat fall ändras inte lagerstatus från artiklarnas nuvarande ',
  },
  { displayValue: 'I lager', value: 'instock' },
  { displayValue: 'Ej i lager', value: 'outofstock' },
]

// -------------------
// -- Reactive vars --
// -------------------
const availableSites = ref<Site[]>([])

const currentFramework = ref<ECommerceFramework>('wc')

const productSyncAction = ref<ProductSyncAction>('update')
const wcProductStatus = ref<WCProductStatus>()
const shopifyProductStatus = ref<ShopifyProductStatus>()
const wcStockStatus = ref<WCStockStatus>()
const shopifyMarketSyncAction = ref<MarketSyncAction>('publish')

const wcSelectedMarketHandlesBySiteId = ref(new Map<string, Set<string>>())
const shopifySelectedMarketHandlesBySiteId = ref(new Map<string, Set<string>>())

const preview = ref<SyncPreview>()
const selloOverwriteWarning = ref<{
  msg: string
  skus: string[]
}>()

const confirmSyncStart = ref(false)
const refreshing = ref(false)
const synching = ref<{
  total?: number
  link?: string
}>({
  total: undefined,
  link: undefined,
})
const loading = ref(true)

const ignoredRules = ref<Partial<Record<IgnorableRule, boolean>>>({})

const hideSyncReport = ref(false)

// --------------
// -- Computed --
// --------------

const progressBars = computed(() => store.activeProgresses['web-sync'])

/** web-sync progress without id */
const mainProgress = computed(() => progressBars.value.find((bar) => !bar.id))
const childProgressIsActive = computed(() => progressBars.value.length > 1)

const actionPending = computed(() => mainProgress.value && !mainProgress.value?.finished)

const selectedStoreNames = computed(() => {
  // console.log(currentSettings.value.selectedMarketHandlesBySiteId)

  const selected = availableSites.value.reduce((acc, site) => {
    if (affectsEntireSite.value) {
      if (currentSettings.value.selectedMarketHandlesBySiteId.get(site.id)) {
        acc.add(site.id)
      }
    } else {
      site.markets.forEach((market) => {
        if (currentSettings.value.selectedMarketHandlesBySiteId.get(site.id)?.has(market.handle)) {
          acc.add(market.name)
        }
      })
    }

    return acc
  }, new Set<string>())

  return Array.from(selected).join(', ')
})

const sitesMatchingCurrentFramework = computed(() => availableSites.value.filter((market) => market.framework === currentFramework.value))

const currentSettings = computed(() =>
  currentFramework.value === 'wc'
    ? {
        get selectedMarketHandlesBySiteId() {
          return wcSelectedMarketHandlesBySiteId.value
        },
        get productStatus() {
          return wcProductStatus.value
        },
        set productStatus(value) {
          wcProductStatus.value = value
        },
        get marketSyncAction() {
          return undefined
        },
        get stockStatus() {
          return wcStockStatus.value
        },
        set stockStatus(value) {
          wcStockStatus.value = value
        },
      }
    : {
        get selectedMarketHandlesBySiteId() {
          return shopifySelectedMarketHandlesBySiteId.value
        },
        get productStatus() {
          return shopifyProductStatus.value
        },
        set productStatus(value) {
          shopifyProductStatus.value = value
        },
        get marketSyncAction() {
          return shopifyMarketSyncAction.value
        },
        set marketSyncAction(value) {
          shopifyMarketSyncAction.value = value
        },
        get stockStatus() {
          return undefined
        },
      },
)

const canStartSync = computed(() => {
  if (
    currentFramework.value === 'shopify' &&
    (!currentSettings.value.marketSyncAction || productSyncAction.value === 'delete') &&
    currentSettings.value.selectedMarketHandlesBySiteId
  ) {
    return !!currentSettings.value.selectedMarketHandlesBySiteId.size
  }

  const sites = currentSettings.value.selectedMarketHandlesBySiteId.values()
  for (let siteMarkets = sites.next(); !siteMarkets.done; siteMarkets = sites.next()) {
    if (siteMarkets.value?.size) {
      return true
    }
  }
  return false
})

const productAndMarketRulesApply = computed(() => productSyncAction.value !== 'delete' && shopifyMarketSyncAction.value !== 'unpublish')

const affectsEntireSite = computed(() => currentFramework.value === 'shopify' && (!shopifyMarketSyncAction.value || productSyncAction.value === 'delete'))

const stagedAction = computed(() => {
  const foundOption =
    currentSettings.value.marketSyncAction === 'unpublish'
      ? marketSyncOptions.find((option) => option.value === 'unpublish')
      : productSyncOptions.find((option) => option.value === productSyncAction.value)

  return foundOption?.displayValue.toLowerCase()
})

const ignoredRulesArray = computed(() =>
  Object.entries(ignoredRules.value).reduce((acc: IgnorableRule[], [rule, state]) => {
    if (state) {
      acc.push(rule as IgnorableRule)
    }
    return acc
  }, []),
)

const uniqueSkippedEntries = computed(() => filterUniqueEntries<{ sku: string; reason?: string }>(mainProgress.value?.data?.skipped, 'sku'))

// --------------
// -- Watchers --
// --------------
watch(
  () => props.activated,
  (activated) => {
    if (activated) {
      refreshing.value = true
      SyncService.getAvailableECommerceSites().then(async (sites) => {
        availableSites.value = sites
        preview.value = await SyncService.getProductsSiteSyncPreview(route.query, ignoredRulesArray.value)
        checkSelloOverwrites()
        refreshing.value = false
        loading.value = false
      })
    }
  },
  { immediate: true },
)

watch(
  () => route.fullPath,
  () => {
    // Close modal if user navigates
    emit('close')
  },
)

watch(
  [() => currentSettings.value, () => productSyncAction.value],
  () => {
    confirmSyncStart.value = false
  },
  { deep: true },
)

const fetchPreviewDebouncer = new Debouncer()
watch(
  ignoredRules,
  () => {
    fetchPreviewDebouncer.debounce(async () => {
      refreshing.value = true
      preview.value = await SyncService.getProductsSiteSyncPreview(route.query, ignoredRulesArray.value)
      refreshing.value = false
    }, 1500)
  },
  { deep: true },
)

// ---------------
// -- Functions --
// ---------------
const startSync = async () => {
  store.clearProgress('web-sync')
  hideSyncReport.value = false

  confirmSyncStart.value = false
  // if (this.total < 100 || confirm(`Är du säker på att du vill synkronisera ${this.total} artiklar till ${this.selectedSubsites.size} olika sajter?`)) {
  if (!selloOverwriteWarning.value || confirm(`${selloOverwriteWarning.value.msg}. Vill du fortsätta?`)) {
    synching.value = {
      total: props.total,
      link: route.fullPath,
    }

    // convert to serializable
    const marketHandlesBySiteId = Array.from(currentSettings.value.selectedMarketHandlesBySiteId).reduce((acc: StringArrayObject, [siteId, markets]) => {
      acc[siteId] = affectsEntireSite.value ? [] : Array.from(markets)
      return acc
    }, {})

    try {
      await SyncService.startECommerceSync({
        productsQuery: route.query,
        includedCountries: Array.from(store.includedCountries),
        includeProductsMissingAvailableCountries: store.includeProductsMissingAvailableCountries,
        productType: store.productType,
        marketHandlesBySiteId: marketHandlesBySiteId,
        productSyncAction: productSyncAction.value,
        shopifyMarketSyncAction: currentSettings.value.marketSyncAction!,
        wcProductStatus: currentFramework.value === 'wc' ? wcProductStatus.value : undefined,
        shopifyProductStatus: currentFramework.value === 'shopify' ? shopifyProductStatus.value : undefined,
        wcStockStatus: currentSettings.value.stockStatus,
        ignoredRules: ignoredRulesArray.value,
      })
    } catch (err: any) {
      console.log(err)
      store.addMessage(new Message('Synken avbröts eller kunde inte starta', err.message, 'danger'))
    } finally {
      synching.value = {}
    }
  }
}

const abort = () => {
  SyncService.abortProductsSync()
}

const toggleAllMarkets = (siteId: string) => {
  const marketSiteHandles = availableSites.value.find((site) => site.id === siteId)?.markets.map((market) => market.handle)

  if (marketSiteHandles) {
    const toggledOn = currentSettings.value.selectedMarketHandlesBySiteId.get(siteId)?.size

    if (toggledOn) {
      currentSettings.value.selectedMarketHandlesBySiteId.delete(siteId)
    } else {
      currentSettings.value.selectedMarketHandlesBySiteId.set(siteId, new Set(marketSiteHandles))
    }
  }
}

const toggleSite = (siteId: string) => {
  if (currentSettings.value.selectedMarketHandlesBySiteId.get(siteId)) {
    currentSettings.value.selectedMarketHandlesBySiteId.delete(siteId)
  } else {
    currentSettings.value.selectedMarketHandlesBySiteId.set(siteId, new Set())
  }
}

const toggleMarket = (siteId: string, market: Market) => {
  confirmSyncStart.value = false

  if (currentSettings.value.selectedMarketHandlesBySiteId?.get(siteId)?.has(market.handle)) {
    currentSettings.value.selectedMarketHandlesBySiteId?.get(siteId)?.delete(market.handle)
  } else {
    if (!currentSettings.value.selectedMarketHandlesBySiteId?.get(siteId)) {
      currentSettings.value.selectedMarketHandlesBySiteId?.set(siteId, new Set())
    }
    currentSettings.value.selectedMarketHandlesBySiteId?.get(siteId)?.add(market.handle)
  }

  if (siteId === SelloSiteId) {
    checkSelloOverwrites()
  }
}

const checkSelloOverwrites = () => {
  const selectedSelloSites = currentSettings.value.selectedMarketHandlesBySiteId.get(SelloSiteId)

  const uniqueSkus = new Set<string>()
  const willBeOverwritten = new Set<string>()
  selectedSelloSites?.forEach((marketHandle) => {
    preview.value?.[SelloSiteId]?.[marketHandle]?.included.forEach((sku) => {
      if (uniqueSkus.has(sku)) {
        willBeOverwritten.add(sku)
      }
      uniqueSkus.add(sku)
    })
  })

  if (willBeOverwritten.size) {
    selloOverwriteWarning.value = {
      msg: `${willBeOverwritten.size} artiklar kommer skrivas över`,
      skus: Array.from(willBeOverwritten),
    }
  } else {
    selloOverwriteWarning.value = undefined
  }
}

const getUniqueSkus = (entryRecord: Record<string, string[]>) => {
  const entries = Object.values(entryRecord).flat()
  return Array.from(new Set(entries))
}
</script>

<style lang="scss" scoped>
.framework-icon {
  max-height: 20px;
  max-width: 30px;
}

section {
  padding: 1em 0;
  margin-bottom: 1em;

  &.settings {
    padding: 1em;
    border: 1px solid #ccc;

    .section-title {
      margin-top: -0.5em;
      margin-left: 0.25em;
      padding-bottom: 1em;
      font-weight: bold;
    }
  }
}

fieldset:disabled {
  opacity: 0.4;
}

hr {
  margin: 1em 0;
  border: 1px dashed #aaa;
}

.columns {
  columns: 1;
  min-height: 250px;

  > :not(:first-child) {
    margin: 1em 0;
  }

  @media (min-width: 768px) {
    columns: 2;
  }
}

.main-site {
  break-inside: avoid;
  background-color: #eee6;
  padding: 0.5em;
}

.preview {
  font-size: 0.8em;

  a {
    color: unset;
  }
}

.site {
  user-select: none;

  &:not(.disabled) {
    .preview {
      .sync {
        color: var(--success) !important;
      }

      .skip {
        color: var(--danger) !important;
      }
    }
  }
}

.warning {
  color: red;
}

.selected {
  font-weight: bold;
  // font-size: 1.3em;

  &.create,
  &.publish {
    color: var(--success);
  }
  &.update {
    color: var(--primary);
  }
  &.delete {
    color: var(--danger);
  }

  &.unpublish {
    color: var(--warning);
  }
}

.auto-sync {
  color: green;
}
em {
  font-weight: 600;
}
</style>
